import {useState, useEffect} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box,
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Autocomplete,
  Divider
} from "@mui/material";
import {Refresh} from "@mui/icons-material";
import {v4 as uuidv4} from "uuid";
import {toast} from "react-toastify";
import {GraphRepository} from "../../../../repositories/GraphRepository";

export const CreateNodeModal = ({ open, onClose }) => {
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState("");
  const [nodeProperties, setNodeProperties] = useState(null);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [formValues, setFormValues] = useState({});

  // Relationship state
  const [relationshipType, setRelationshipType] = useState("");
  const [targetNodeLabel, setTargetNodeLabel] = useState("");
  const [targetNodeId, setTargetNodeId] = useState("");
  const [targetNodes, setTargetNodes] = useState({});
  const [targetNodesLoading, setTargetNodesLoading] = useState(false);

  // Fetch schema when modal opens
  useEffect(() => {
    if (open) {
      setLoading(true);
      GraphRepository.getGraphSchema()
        .then((response) => {
          setSchema(response.data);
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching schema:", error);
          setLoading(false);
        });
    } else {
      // Reset state when modal closes (but preserve targetNodes cache)
      setSchema(null);
      setSelectedLabel("");
      setNodeProperties(null);
      setFormValues({});
      setRelationshipType("");
      setTargetNodeLabel("");
      setTargetNodeId("");
      // Don't reset targetNodes to preserve the cache
    }
  }, [open]);

  // Fetch nodes when target node label is selected
  useEffect(() => {
    if (targetNodeLabel) {
      setTargetNodeId(""); // Reset selected node ID

      // Only fetch if not already cached
      if (!targetNodes[targetNodeLabel]) {
        setTargetNodesLoading(true);
        GraphRepository.getNodesForLabel(targetNodeLabel)
          .then((response) => {
            setTargetNodes((prev) => ({
              ...prev,
              [targetNodeLabel]: response.data?.nodes || []
            }));
            setTargetNodesLoading(false);
          })
          .catch((error) => {
            console.error("Error fetching target nodes:", error);
            setTargetNodes((prev) => ({
              ...prev,
              [targetNodeLabel]: []
            }));
            setTargetNodesLoading(false);
          });
      }
    } else {
      setTargetNodeId("");
    }
  }, [targetNodeLabel]);

  // Fetch node properties when label is selected
  useEffect(() => {
    if (selectedLabel) {
      setPropertiesLoading(true);
      GraphRepository.getNodeProperties(selectedLabel)
        .then((response) => {
          setNodeProperties(response.data);
          // Initialize form values with default values based on type
          const initialValues = {};
          response.data.properties?.forEach((prop) => {
            if (prop.type === "boolean") {
              initialValues[prop.name] = false;
            } else {
              initialValues[prop.name] = "";
            }
          });
          setFormValues(initialValues);
          setPropertiesLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching node properties:", error);
          setPropertiesLoading(false);
        });
    } else {
      setNodeProperties(null);
      setFormValues({});
    }
  }, [selectedLabel]);

  const handleInputChange = (propertyName, value) => {
    setFormValues((prev) => ({
      ...prev,
      [propertyName]: value
    }));
  };

  const handleGenerateRandomId = (name, type) => {
    let randomValue;
    if (type === "integer" || type === "int" || type === "float") {
      // Generate random number from 100000+
      randomValue = Math.floor(Math.random() * 900000) + 100000;
    } else {
      // Generate UUID for string type
      randomValue = uuidv4();
    }
    handleInputChange(name, randomValue);
  };

  const handleCreate = async () => {
    // Convert form values to appropriate types
    const properties = {};
    nodeProperties.properties.forEach((prop) => {
      const value = formValues[prop.name];
      if (value !== "" && value !== null && value !== undefined) {
        if (prop.type === "integer" || prop.type === "int") {
          properties[prop.name] = parseInt(value, 10);
        } else if (prop.type === "float") {
          properties[prop.name] = parseFloat(value);
        } else if (prop.type === "boolean") {
          properties[prop.name] = Boolean(value);
        } else if (prop.type === "date" || prop.type === "datetime") {
          // Keep date as string in ISO format
          properties[prop.name] = value;
        } else {
          // String and other types
          properties[prop.name] = String(value);
        }
      }
    });

    const toastId = toast.loading("Creating node...");

    try {
      // Create the node first
      const response = await GraphRepository.createNode(selectedLabel, properties);
      const createdNodeId = properties.id || response.data?.id;

      console.log("Node created:", response.data);

      // Create relationship if all required fields are filled
      if (relationshipType && targetNodeLabel && targetNodeId && createdNodeId) {
        toast.update(toastId, {
          render: "Creating relationship...",
          isLoading: true
        });

        await GraphRepository.createRelationship(
          selectedLabel,
          createdNodeId,
          targetNodeLabel,
          targetNodeId,
          relationshipType
        );

        toast.update(toastId, {
          render: "Node and relationship created successfully!",
          type: "success",
          isLoading: false,
          autoClose: 3000
        });
      } else {
        toast.update(toastId, {
          render: "Node created successfully!",
          type: "success",
          isLoading: false,
          autoClose: 3000
        });
      }

      onClose();
    } catch (error) {
      toast.update(toastId, {
        render: error?.response?.data?.message || "Failed to create node",
        type: "error",
        isLoading: false,
        autoClose: 5000
      });
      console.error("Error creating node:", error);
    }
  };

  const renderField = (property) => {
    const { name, type } = property;

    // Check if this is an ID field
    const isIdField = name.toLowerCase() === "id";

    // Get placeholder from first sample if exists, otherwise use property name
    const placeholder = nodeProperties?.samples?.[0]?.[name] !== undefined
      ? String(nodeProperties.samples[0][name])
      : name;

    if (type === "boolean") {
      return (
        <Box key={name}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formValues[name] || false}
                onChange={(e) => handleInputChange(name, e.target.checked)}
              />
            }
            label={name}
          />
        </Box>
      );
    }

    if (type === "integer" || type === "float" || type === "int") {
      return (
        <Box key={name}>
          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
            {name}
          </Typography>
          <TextField
            fullWidth
            type="number"
            value={formValues[name] || ""}
            onChange={(e) => handleInputChange(name, e.target.value)}
            placeholder={placeholder}
            inputProps={{
              step: type === "float" ? "0.01" : "1"
            }}
            InputProps={isIdField ? {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleGenerateRandomId(name, type)}
                    edge="end"
                    title="Generate Random ID"
                  >
                    <Refresh />
                  </IconButton>
                </InputAdornment>
              )
            } : undefined}
          />
        </Box>
      );
    }

    if (type === "date" || type === "datetime") {
      return (
        <Box key={name}>
          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
            {name}
          </Typography>
          <TextField
            fullWidth
            type={type === "datetime" ? "datetime-local" : "date"}
            value={formValues[name] || ""}
            onChange={(e) => handleInputChange(name, e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Box>
      );
    }

    // Default to text field for string and other types
    return (
      <Box key={name}>
        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
          {name}
        </Typography>
        <TextField
          fullWidth
          type="text"
          value={formValues[name] || ""}
          onChange={(e) => handleInputChange(name, e.target.value)}
          placeholder={placeholder}
          multiline={name === "description"}
          rows={name === "description" ? 4 : 1}
          InputProps={isIdField ? {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => handleGenerateRandomId(name, type)}
                  edge="end"
                  title="Generate Random ID"
                >
                  <Refresh />
                </IconButton>
              </InputAdornment>
            )
          } : undefined}
        />
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create Node</DialogTitle>
      <DialogContent sx={{minHeight: "40dvh"}}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Autocomplete
              fullWidth
              sx={{ mt: 2 }}
              options={schema?.node_labels || []}
              value={selectedLabel}
              onChange={(event, newValue) => {
                setSelectedLabel(newValue || "");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Node Label"
                  placeholder="Select or type node label"
                />
              )}
            />

            {propertiesLoading && (
              <Box display="flex" justifyContent="center" alignItems="center" mt={3}>
                <CircularProgress size={24} />
              </Box>
            )}

            {nodeProperties && !propertiesLoading && (
              <>
                <Box mt={3}>
                  <Typography variant="subtitle1" gutterBottom>
                    Properties for {selectedLabel}:
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {nodeProperties.properties?.map((property) => renderField(property))}
                  </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Create Relationship (Optional)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Optionally create a relationship where the new node is the Subject, the relationship type is the Predicate, and you select an existing node as the Object.
                  </Typography>

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Autocomplete
                      freeSolo
                      options={schema?.relationship_types || []}
                      value={relationshipType}
                      onChange={(event, newValue) => {
                        setRelationshipType(newValue ? newValue.toUpperCase() : "");
                      }}
                      onInputChange={(event, newInputValue) => {
                        setRelationshipType(newInputValue.toUpperCase());
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Relationship Type (Predicate)"
                          placeholder="Select or type relationship type"
                        />
                      )}
                    />

                    <Autocomplete
                      fullWidth
                      options={schema?.node_labels || []}
                      value={targetNodeLabel}
                      onChange={(event, newValue) => {
                        setTargetNodeLabel(newValue || "");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Target Node Label"
                          placeholder="Select or type target node label"
                        />
                      )}
                    />

                    {targetNodesLoading ? (
                      <Box display="flex" justifyContent="center" alignItems="center" py={2}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : targetNodeLabel && targetNodes[targetNodeLabel]?.length > 0 ? (
                      <Autocomplete
                        fullWidth
                        options={targetNodes[targetNodeLabel] || []}
                        value={targetNodes[targetNodeLabel]?.find(node => node?.properties?.id === targetNodeId) || null}
                        onChange={(event, newValue) => {
                          setTargetNodeId(newValue?.properties?.id || "");
                        }}
                        getOptionLabel={(option) =>
                          `${option?.properties?.name || option?.properties?.title || option?.properties?.id} (ID: ${option?.properties?.id})`
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Target Node (Object)"
                            placeholder="Select target node"
                          />
                        )}
                      />
                    ) : targetNodeLabel ? (
                      <Typography variant="body2" color="text.secondary">
                        No nodes found for label "{targetNodeLabel}"
                      </Typography>
                    ) : null}
                  </Box>
                </Box>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="modal" onClick={handleCreate} disabled={!selectedLabel}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};